import {RankedModeTypes} from '../../../../shared/GameEnums'
import {LeaderboardRowData, LeaderboardStickyRowData, LeaderboardTabData} from '../../../../shared/leaderboard_data/LeaderboardInterfaces'
import {UIAjaxWrapper} from '../../../shared/scripts/network/UIAjaxWrapper'
import {UILeaderboardHorizontalTabsController} from './UILeaderboardHorizontalTabsController'
import {UILeaderboardTableController} from '../../../shared/scripts/ui_leaderboard/UILeaderboardTableController.ts'
import {UILeaderboardStickyRowController} from '../../../shared/scripts/ui_leaderboard/UILeaderboardStickyRowController.ts'
import {LeaderboardConstants} from '../../../../shared/leaderboard_data/LeaderboardConstants'
import {UILeaderboardSidebarTabsController} from '../../../shared/scripts/ui_leaderboard/UILeaderboardSidebarTabsController.ts'
import {UIPopupController} from '../../../shared/scripts/ui_popup/UIPopupController'
import {UILeaderboardSeasonProgressController} from '../../../shared/scripts/ui_leaderboard/UILeaderboardSeasonProgressController'
import {UIAPIEndpoints} from '../../../shared/scripts/network/UIAPIEndpoints'
import {RankedSeasonState} from '../../../../shared/ranked/ranked_season_data/RankedSeasonState'
import {UIComponentSearchController} from '../../../shared/scripts/ui_components/UIComponentSearchController'
import {UIWLeaderboardCTAController} from './UIWLeaderboardCTAController'
import {TimeUtils} from '../../../../shared/utils/TimeUtils'
import {LeaderboardCategoryStringID} from '../../../../shared/leaderboard_data/LeaderboardEnums'
import {clientI18n, clientIntl} from '../../../shared/scripts/ClientI18n'
import {TimeoutId} from '@colonist/types'

import {navigateToPage} from '../../../shared/scripts/ui_utils/ClientUtils.ts'
import {UIUtilsEjsVariables} from '../../../shared/scripts/UIUtilsEjsVariables.ts'

interface LeaderboardHistoryState {
    rankedModeType: RankedModeTypes
    leaderboardUrl: string
    searchTerm: string
}

export class UIWLeaderboardController {
    protected leaderboardUrl: string = LeaderboardConstants.globalLeaderboardUrl
    protected rankedModeType: RankedModeTypes = RankedModeTypes.Classic4P
    protected tabsController: UILeaderboardHorizontalTabsController
    protected tableController: UILeaderboardTableController
    protected stickyRowController: UILeaderboardStickyRowController
    protected searchController: UIComponentSearchController
    protected sidebarTabsController: UILeaderboardSidebarTabsController
    protected highlightRowElement: HTMLDivElement
    protected ctaController: UIWLeaderboardCTAController
    protected searchTerm: string
    private isLoadingDataAfterScroll: boolean = false
    private waitForBackend: TimeoutId | undefined

    constructor() {
        UIWLeaderboardController.featureCheck()
        this.stickyRowController = new UILeaderboardStickyRowController()
        this.tableController = new UILeaderboardTableController(() => this.fetchScrollData(), this.stickyRowController)
        this.tabsController = new UILeaderboardHorizontalTabsController((leaderboardUrl: string) => this.onHorizontalTabChange(leaderboardUrl))
        this.sidebarTabsController = new UILeaderboardSidebarTabsController((rankedMode: RankedModeTypes) => this.onRankedModeChange(rankedMode))
        this.highlightRowElement = document.querySelector('.leaderboard_highlight_row') as HTMLDivElement
        this.initCTAController()

        this.initiateParametersFromUrl()
        void this.fetchNewTabs()
        void this.fetchMyStickyRowData()

        this.searchController = new UIComponentSearchController('leaderboard_search', this.searchTerm, (searchValue: string) => this.onSearchChange(searchValue))
        this.stickyRowController.setSearchValue(this.searchTerm)

        this.switchRankingCategoryTypeData(this.rankedModeType, this.leaderboardUrl, this.searchTerm, false)
        window.addEventListener('popstate', (event: PopStateEvent) => this.onPopState(event))
    }

    protected initCTAController() {
        this.ctaController = new UIWLeaderboardCTAController()
    }

    protected onSearchChange(searchValue: string) {
        this.switchRankingCategoryTypeData(this.rankedModeType, this.leaderboardUrl, searchValue)
        void this.fetchMyStickyRowData()
        this.stickyRowController.setSearchValue(searchValue)
        this.tableController.updateStickyRowStyle()
    }

    protected onRankedModeChange(rankedMode: RankedModeTypes) {
        this.switchRankingCategoryTypeData(rankedMode, this.leaderboardUrl, this.searchTerm)
        void this.fetchMyStickyRowData()
    }

    protected onHorizontalTabChange(leaderboardUrl: string) {
        this.switchRankingCategoryTypeData(this.rankedModeType, leaderboardUrl, this.searchTerm)
        if(leaderboardUrl == LeaderboardConstants.globalLeaderboardUrl) void this.fetchNewTabs()
        void this.fetchMyStickyRowData()
    }

    private onLeaderboardUrlChange(newLeaderboardUrl: string) {
        this.switchRankingCategoryTypeData(this.rankedModeType, newLeaderboardUrl, this.searchTerm)
        void this.fetchNewTabs()
        void this.fetchMyStickyRowData()
    }

    protected initiateParametersFromUrl() {
        const url = new URL(window.location.href)
        const pathSegments = url.pathname.split('/')
        const leaderboardPathSegmentIndex = pathSegments.indexOf('leaderboards')
        const rankedModeType = pathSegments[leaderboardPathSegmentIndex + 1]
        const leaderboardUrl = pathSegments.slice(leaderboardPathSegmentIndex + 2).join('/')
        if(RankedModeTypes[rankedModeType] !== undefined) this.rankedModeType = RankedModeTypes[rankedModeType]
        this.leaderboardUrl = leaderboardUrl ?? LeaderboardConstants.globalLeaderboardUrl
        this.searchTerm = url.searchParams.get('q') ?? ''
    }

    protected onPopState(event: PopStateEvent) {
        const state: LeaderboardHistoryState = event.state

        if(state == undefined) {
            this.initiateParametersFromUrl()
            this.switchRankingCategoryTypeData(this.rankedModeType, this.leaderboardUrl, this.searchTerm, false)
            return
        }

        this.switchRankingCategoryTypeData(state.rankedModeType, state.leaderboardUrl, state.searchTerm, false)
    }

    private getUrl(): string {
        const searchQuery = this.searchTerm === '' ? '' : `?q=${encodeURIComponent(this.searchTerm)}`
        const thisUrl = this.leaderboardUrl ? `/${this.leaderboardUrl}` : ''
        return `${clientIntl.getLanguagePathPrefix()}/leaderboards/${RankedModeTypes[this.rankedModeType]}${thisUrl}${searchQuery}`
    }

    protected switchRankingCategoryTypeData(mode: RankedModeTypes, regionId: string, search: string, push: boolean = true) {
        this.rankedModeType = mode
        this.leaderboardUrl = regionId
        this.searchTerm = search

        if(!UIUtilsEjsVariables.isUserOnDiscord) {
            this.updateState(push)
            this.updateBrowserCaption()
        }

        this.sidebarTabsController.highlightTab(this.rankedModeType)
        this.tabsController.highlightTab(this.leaderboardUrl)
        this.tableController.clearData()
        this.tableController.scrollToTop()
        this.searchTerm == LeaderboardConstants.emptyUsernameSearch ? void this.fetchScrollData() : void this.fetchSearchData()
    }

    protected updateBrowserCaption() {
        const thisUrl = this.leaderboardUrl ? `/${this.leaderboardUrl}` : ''
        const pageName = `${RankedModeTypes[this.rankedModeType]}${thisUrl}`
        document.title = clientI18n.t('strings:leaderboardPage.pageHeadInfo.title', {pageName: pageName, interpolation: {escapeValue: false}})
    }

    protected updateState(push: boolean) {
        const newState: LeaderboardHistoryState = {rankedModeType: this.rankedModeType, leaderboardUrl: this.leaderboardUrl, searchTerm: this.searchTerm}
        push ? history.pushState(newState, '', this.getUrl()) : history.replaceState(newState, '', this.getUrl())
    }

    protected async fetchScrollData() {
        const isActiveSearchOngoing = this.searchTerm != '' && this.tableController.getRowCount() > 0
        if(isActiveSearchOngoing) return

        if(this.isLoadingDataAfterScroll) return
        this.startWaitingForBackendToRespond()
        const startRank = this.tableController.getRowCount() + 1
        const endRank = startRank + LeaderboardConstants.maximumLeaderboardAPIRankingRangeLimit - 1

        const data = await UIAjaxWrapper.getRequestHandler(UIAPIEndpoints.apiLeaderboards.data(RankedModeTypes[this.rankedModeType], this.leaderboardUrl), undefined, {
            start: startRank,
            end: endRank,
            search: this.searchTerm,
        })
        this.stopWaitingForBackendToRespond()
        if(data == undefined) return

        if(this.leaderboardUrl == LeaderboardCategoryStringID.Friends && data.json.length == 0) {
            this.tableController.displayNoContentMessage()
            return
        }

        const leaderboardRowData = data.json as LeaderboardRowData[]
        if(leaderboardRowData.length == 0) return
        if(!this.isReturnedDataInValidOrder(leaderboardRowData)) {
            await this.fetchScrollData()
            return
        }

        this.tableController.updateRankingData(leaderboardRowData, (leaderboardUrl: string) => this.onLeaderboardUrlChange(leaderboardUrl))
        this.stickyRowController.tableRowLoadingCompleted()
        this.tableController.updateStickyRowStyle()
    }

    private isReturnedDataInValidOrder(leaderboardRowData: LeaderboardRowData[]): boolean {
        if(this.leaderboardUrl == LeaderboardCategoryStringID.Friends) return true
        const firstRankRetured = leaderboardRowData[0].rank as number
        const expectedFirstRankReturned = this.tableController.getRowCount() + 1
        if(firstRankRetured == expectedFirstRankReturned) return true
        return false
    }

    protected async fetchSearchData() {
        this.startWaitingForBackendToRespond()
        const data = await UIAjaxWrapper.getRequestHandler(UIAPIEndpoints.apiLeaderboards.usernameSearch(RankedModeTypes[this.rankedModeType], this.leaderboardUrl), undefined, {
            usernameSearchInput: this.searchTerm,
        })
        this.stopWaitingForBackendToRespond()
        if(data == undefined) return

        this.tableController.clearData()
        this.tableController.scrollToTop()
        this.tableController.updateRankingData(data.json as LeaderboardRowData[], (leaderboardUrl: string) => this.onLeaderboardUrlChange(leaderboardUrl))
        this.stickyRowController.tableRowLoadingCompleted()
        this.tableController.updateStickyRowStyle()
    }

    private startWaitingForBackendToRespond() {
        this.isLoadingDataAfterScroll = true
        const maxTimeToWaitForBackend = TimeUtils.MILLISECONDS
        this.waitForBackend = setTimeout(() => {
            this.stopWaitingForBackendToRespond()
        }, maxTimeToWaitForBackend)
    }

    private stopWaitingForBackendToRespond() {
        this.isLoadingDataAfterScroll = false
        if(this.waitForBackend != undefined) clearTimeout(this.waitForBackend)
    }

    protected async fetchNewTabs() {
        const response = await UIAjaxWrapper.getRequestHandler(UIAPIEndpoints.apiLeaderboards.tabs(this.leaderboardUrl), undefined)
        if(response == undefined) return
        const leaderboardTabData = response.json as LeaderboardTabData
        if(leaderboardTabData.activeSeasonData != undefined) leaderboardTabData.activeSeasonData = RankedSeasonState.deserialize(leaderboardTabData.activeSeasonData)
        this.updateTabData(response.json as LeaderboardTabData)
    }

    private updateTabData(availableTabs: LeaderboardTabData) {
        this.tabsController.updateLeaderboardRegionTabs(availableTabs.availableHorizontalTabs, this.leaderboardUrl)
        this.sidebarTabsController.setEnabledRankedModeTypes(availableTabs.activeRankedModeTypes)
        if(availableTabs.activeSeasonData != undefined) new UILeaderboardSeasonProgressController(availableTabs.activeSeasonData)
    }

    protected async fetchMyStickyRowData() {
        const data = await UIAjaxWrapper.getRequestHandler(UIAPIEndpoints.apiLeaderboards.stickyRowData(RankedModeTypes[this.rankedModeType], this.leaderboardUrl), undefined)
        if(data == undefined) return

        const myStickyRowData = data.json as LeaderboardStickyRowData | ''
        myStickyRowData == '' ? this.stickyRowController.clearStickyRowData() : this.stickyRowController.updateStickyRowData(myStickyRowData, this.tableController.tableContainer.clientHeight)
    }

    protected static featureCheck() {
        const div = document.createElement('div')
        if(div.replaceChildren == undefined) {
            UIPopupController.create({key: 'strings:leaderboardPage.updateBrowser.title'}, {key: 'strings:leaderboardPage.updateBrowser.body'},
                () => navigateToPage('/'), () => navigateToPage('/'), false)
            throw new Error('Failed to create leaderboard: Browser does not support required features.')
        }
    }
}
